import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useState } from "react"

const Slider = ({ slides }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const currentSlide = slides[activeIndex]
  const Slide = slides[activeIndex]?.component
  const timer = currentSlide?.timer

  const nextSlide = () => {
    if (activeIndex < slides.length - 1) {
      setActiveIndex(activeIndex + 1)
    }
  }

  const skipToEnd = () => {
    setActiveIndex(slides.length - 1)
  }

  useEffect(() => {
    if (!timer) return
    const timeout = setTimeout(() => {
      nextSlide()
    }, timer * 1000)

    return () => clearTimeout(timeout)
  }, [activeIndex])

  const pageVariants = {
    initial: {
      x: 0,
      opacity: 0,
      scale: 0.95,
    },
    in: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "tween",
        ease: "easeOut",
        duration: 0.4,
      },
    },
    out: (direction) => ({
      x: direction > 0 ? "-100%" : "100%",
      opacity: 0,
      scale: 0.95,
      transition: {
        type: "tween",
        ease: "easeIn",
        duration: 0.3,
      },
    }),
  }

  return (
    <div className="h-screen overflow-hidden relative">
      {/* Progress Indicator */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-sm">
        <div className="flex h-1">
          {slides.map((_, index) => (
            <div
              key={index}
              className={`flex-1 transition-all duration-300 ${index <= activeIndex ? "bg-white" : "bg-white/30"}`}
            />
          ))}
        </div>
      </div>

      {/* Slide Counter */}
      <div className="fixed top-4 left-4 z-50 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
        {activeIndex + 1} / {slides.length}
      </div>

      <AnimatePresence initial={false} mode="wait" custom={1}>
        <motion.div
          key={currentSlide?.id}
          custom={1}
          initial="initial"
          animate="in"
          exit="out"
          variants={pageVariants}
          className="h-screen overflow-hidden"
        >
          {currentSlide?.type === "image" && (
            <div className="h-screen relative bg-gray-900 flex items-center justify-center cursor-pointer overflow-hidden">
              {/* Blurred Background */}
              <div className="absolute inset-0">
                <img
                  src={currentSlide?.imgSrc || "/placeholder.svg"}
                  alt="Background"
                  className="w-full h-full object-cover object-center scale-110 blur-lg"
                />
                <div className="absolute inset-0 bg-black/50"></div>
              </div>

              {/* Main Image Content */}
              <div className="relative z-10 flex flex-col items-center justify-center h-full px-6" onClick={nextSlide}>
                <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl">
                  <img
                    src={currentSlide?.imgSrc || "/placeholder.svg"}
                    alt={`Slide ${activeIndex + 1}`}
                    className="w-full max-w-sm mx-auto rounded-2xl shadow-2xl"
                  />
                </div>
              </div>

              {/* Tap to continue indicator */}
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/80 text-center">
                <div className="animate-bounce mb-2">
                  <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
                <p className="text-sm font-medium bg-black/30 backdrop-blur-sm px-3 py-1 rounded-full">
                  Tap to continue
                </p>
              </div>
            </div>
          )}

          {currentSlide?.type === "custom" && <Slide nextSlide={nextSlide} skipToEnd={skipToEnd} imgSrc={slides[activeIndex].imgSrc} />}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default Slider
